// apps/smart-shikka-web/src/pages/Help.tsx
// CORE-HELP-02: canonical Help Center + My Tickets — Core is the single support authority.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../auth.js";
import { useSupportAccess } from "../supportAccess.js";
import { AppNav } from "../components/AppNav.js";
import { useBrandColors } from "@smart-shikka/react";
import {
  supportApi,
  type SupportTicket,
  type SupportTicketDetail,
  type SupportTicketStatus,
} from "../api.js";

const CATEGORIES = [
  "product_help", "bug", "deployment_domain", "integration_provider",
  "account_access", "billing_payment", "security_privacy", "data_loss",
  "feature_request", "other",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  product_help: "प्रोडक्ट सहायता",
  bug: "बग रिपोर्ट",
  deployment_domain: "डिप्लॉय / डोमेन",
  integration_provider: "इंटीग्रेशन",
  account_access: "अकाउंट एक्सेस",
  billing_payment: "बिलिंग / पेमेंट",
  security_privacy: "सुरक्षा / प्राइवेसी",
  data_loss: "डेटा लॉस",
  feature_request: "फ़ीचर रिक्वेस्ट",
  other: "अन्य",
};

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: "खुला",
  human_needed: "मानव सहायता",
  in_progress: "प्रगति में",
  waiting_customer: "आपके उत्तर की प्रतीक्षा",
  resolved: "हल हुआ",
  closed: "बंद",
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "कम", normal: "सामान्य", high: "उच्च", urgent: "तुरंत",
};

function StatusChip({ status, C }: { status: SupportTicketStatus; C: ReturnType<typeof useBrandColors> }) {
  const hot = status === "human_needed" || status === "in_progress";
  const warm = status === "waiting_customer";
  const done = status === "resolved" || status === "closed";
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600, whiteSpace: "nowrap",
      background: done ? C.successBg : warm ? C.warningBg : hot ? C.warningBg : C.panel,
      color: done ? C.successFg : warm ? C.warning : hot ? C.warning : C.muted,
    }}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function fmtTs(ts: number | string | undefined | null): string {
  if (ts === undefined || ts === null || ts === "") return "—";
  const ms = typeof ts === "number" ? ts : Number(ts);
  const d = Number.isFinite(ms) ? new Date(ms) : new Date(String(ts));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("hi-IN", { day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("hi-IN", { hour: "2-digit", minute: "2-digit" });
}

const inputStyle = (C: ReturnType<typeof useBrandColors>): React.CSSProperties => ({
  width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.bg, fontSize: 12, color: C.fg, outline: "none",
  boxSizing: "border-box", fontFamily: "inherit",
});

const btnStyle = (C: ReturnType<typeof useBrandColors>, primary = false): React.CSSProperties => ({
  padding: "7px 14px", borderRadius: 6, border: primary ? "none" : `1px solid ${C.border}`,
  background: primary ? C.accent : "transparent", color: primary ? C.accentFg : C.fg,
  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
});

function CreateTicketForm({ token, onCreated }: { token: string; onCreated: (t: SupportTicket) => void }) {
  const C = useBrandColors();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<string>("product_help");
  const [human, setHuman] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!subject.trim() || !body.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await supportApi.createTicket(token, {
        subject: subject.trim(),
        body: body.trim(),
        category,
        ...(human ? { explicitHumanRequest: true } : {}),
        context: { route: window.location.hash || "/" },
        idempotencyKey: `help-${Date.now().toString(36)}`,
      });
      setSubject(""); setBody(""); setHuman(false);
      onCreated(res.ticket);
    } catch (e) {
      setError(e instanceof Error ? e.message : "टिकट बनाने में विफल");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, padding: "16px 18px", marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.fg, marginBottom: 4 }}>नया सपोर्ट टिकट</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
        पहले Smart Shikka चैट में पूछें — ज्यादातर प्रश्न वहीं हल हो जाते हैं। मानव सहायता चाहिए तो टिकट बनाएँ।
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="विषय — एक लाइन में समस्या" style={inputStyle(C)} maxLength={200} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="विस्तार से बताएँ — क्या कर रहे थे, क्या हुआ, क्या उम्मीद थी" rows={4} style={{ ...inputStyle(C), resize: "vertical" }} maxLength={16000} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle(C), width: "auto" }}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.fg, cursor: "pointer" }}>
            <input type="checkbox" checked={human} onChange={(e) => setHuman(e.target.checked)} />
            मानव सहायक से बात करनी है
          </label>
          <button onClick={submit} disabled={busy || !subject.trim() || !body.trim()} style={{ ...btnStyle(C, true), opacity: busy || !subject.trim() || !body.trim() ? 0.5 : 1 }}>
            {busy ? "भेज रहे हैं..." : "टिकट बनाएँ"}
          </button>
        </div>
        {error && <div style={{ fontSize: 12, color: C.destructive }}>{error}</div>}
      </div>
    </div>
  );
}

function TicketDetail({ token, ticket, onChanged, onBack }: {
  token: string;
  ticket: SupportTicket;
  onChanged: (t: SupportTicket) => void;
  onBack: () => void;
}) {
  const C = useBrandColors();
  const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const load = useCallback(() => {
    supportApi.ticketDetail(token, ticket.ticketId)
      .then(setDetail)
      .catch(() => setError("टिकट लोड नहीं हुआ"));
  }, [token, ticket.ticketId]);

  useEffect(load, [load]);

  const act = async (fn: () => Promise<{ ticket: SupportTicket }>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      onChanged(res.ticket);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "कार्रवाई विफल");
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || busy) return;
    setBusy(true);
    try {
      await supportApi.reply(token, ticket.ticketId, reply.trim());
      setReply("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "उत्तर विफल");
    } finally {
      setBusy(false);
    }
  };

  /** Download via GET /api/support/tickets/:id/attachments/:attachmentId —
   *  server checks actor + ticket + owner/tenant before returning bytes. */
  const downloadAttachment = async (a: { attachmentId: string; filename: string; mime: string }) => {
    setFileError(null);
    try {
      const r = await supportApi.downloadAttachment(token, t.ticketId, a.attachmentId);
      const bytes = Uint8Array.from(atob(r.contentBase64), (ch) => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: a.mime }));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setFileError(e instanceof Error ? e.message : "डाउनलोड विफल");
    }
  };

  const uploadFile = async (file: File) => {
    setFileError(null);
    if (file.size > 8 * 1024 * 1024) { setFileError("फ़ाइल 8 MB से छोटी होनी चाहिए"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = String(reader.result);
        const contentBase64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
        await supportApi.attachFile(token, ticket.ticketId, { filename: file.name, mime: file.type, contentBase64 });
        load();
      } catch (e) {
        setFileError(e instanceof Error ? e.message : "अपलोड विफल");
      }
    };
    reader.readAsDataURL(file);
  };

  const t = detail?.ticket ?? ticket;
  const terminal = t.status === "resolved" || t.status === "closed";

  return (
    <div>
      <button onClick={onBack} style={{ ...btnStyle(C), marginBottom: 14 }}>← मेरे टिकट</button>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: C.fg, margin: 0 }}>{t.subject}</h2>
        <StatusChip status={t.status} C={C} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
        {t.ticketNumber} · {CATEGORY_LABELS[t.category] ?? t.category} · {PRIORITY_LABELS[t.priority] ?? t.priority} · {fmtTs(t.createdAt)}
        {t.reopenedCount > 0 ? ` · ${t.reopenedCount}× reopen` : ""}
      </div>

      {error && <div style={{ fontSize: 12, color: C.destructive, marginBottom: 10 }}>{error}</div>}

      {/* Thread */}
      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        {(detail?.messages ?? [])
          .filter((m) => m.kind === "customer_message" || m.kind === "human_reply")
          .map((m) => (
            <div key={m.messageId} style={{
              border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px",
              background: m.kind === "human_reply" ? C.accent : C.card,
              marginLeft: m.kind === "human_reply" ? 24 : 0,
              marginRight: m.kind === "human_reply" ? 0 : 24,
            }}>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>
                {m.kind === "human_reply" ? "सपोर्ट टीम" : "आप"} · {fmtTs(m.createdAt)}
              </div>
              <div style={{ fontSize: 12, color: C.fg, whiteSpace: "pre-wrap" }}>{m.body}</div>
            </div>
          ))}
      </div>

      {/* Attachments — downloaded only through the canonical ticket-authorized
          route; no public artifact URL is ever rendered. */}
      {(detail?.attachments ?? []).length > 0 && (
        <div style={{ marginBottom: 14, fontSize: 11, color: C.muted, display: "flex", gap: 10, flexWrap: "wrap" }}>
          अटैचमेंट:
          {(detail?.attachments ?? []).map((a) => (
            <button key={a.attachmentId} onClick={() => void downloadAttachment(a)} style={{
              border: "none", background: "transparent", color: C.accentFg, fontSize: 11,
              cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0,
            }}>{a.filename}</button>
          ))}
        </div>
      )}

      {/* Actions */}
      {!terminal && (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="उत्तर लिखें..." rows={2} style={{ ...inputStyle(C), flex: 1, resize: "vertical" }} maxLength={16000} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={sendReply} disabled={busy || !reply.trim()} style={{ ...btnStyle(C, true), opacity: !reply.trim() ? 0.5 : 1 }}>उत्तर भेजें</button>
            <label style={{ ...btnStyle(C), cursor: "pointer", fontSize: 11 }}>
              फ़ाइल जोड़ें
              <input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.log" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }} />
            </label>
            <button onClick={() => act(() => supportApi.resolve(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>हल हो गया — बंद करें</button>
            {t.status !== "human_needed" && (
              <button onClick={() => act(() => supportApi.escalate(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>मानव सहायता चाहिए</button>
            )}
          </div>
          {fileError && <div style={{ fontSize: 11, color: C.destructive }}>{fileError}</div>}
        </div>
      )}

      {/* Resolved — feedback + reopen same ticket */}
      {terminal && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "12px 14px", border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: C.fg }}>क्या समस्या हल हुई?</span>
          <button onClick={() => act(() => supportApi.feedback(token, t.ticketId, true))} disabled={busy} style={btnStyle(C)}>👍 हाँ</button>
          <button onClick={() => act(() => supportApi.feedback(token, t.ticketId, false))} disabled={busy} style={btnStyle(C)}>👎 नहीं</button>
          <button onClick={() => act(() => supportApi.reopen(token, t.ticketId))} disabled={busy} style={btnStyle(C)}>दोबारा खोलें</button>
        </div>
      )}
    </div>
  );
}

export function HelpPage() {
  const { token } = useAuth();
  const { access } = useSupportAccess();
  const C = useBrandColors();
  const [tab, setTab] = useState<"help" | "tickets">("help");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [notifs, setNotifs] = useState<import("../api.js").SupportNotification[]>([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(() => {
    if (!token) { setLoading(false); return; }
    supportApi.listTickets(token)
      .then((r) => setTickets(r.tickets))
      .catch(() => {})
      .finally(() => setLoading(false));
    supportApi.notifications(token)
      .then((r) => { setNotifs(r.notifications); setUnread(r.unread); })
      .catch(() => {});
  }, [token]);

  useEffect(refresh, [refresh]);

  // Deep link: #/help?ticket=<id> opens that ticket straight away (§6).
  const deepLinkTicket = useMemo(() => {
    const q = window.location.hash.split("?")[1];
    return new URLSearchParams(q ?? "").get("ticket");
  }, []);

  useEffect(() => {
    if (!deepLinkTicket || tickets.length === 0 || selected) return;
    const t = tickets.find((x) => x.ticketId === deepLinkTicket);
    if (t) { setSelected(t); setTab("tickets"); }
  }, [deepLinkTicket, tickets, selected]);

  const openNotificationTicket = async (n: import("../api.js").SupportNotification) => {
    if (token && n.readAt === undefined) {
      await supportApi.markNotificationsRead(token, [n.notificationId]).catch(() => undefined);
      setUnread((u) => Math.max(0, u - 1));
      setNotifs((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, readAt: Date.now() } : x)));
    }
    const t = tickets.find((x) => x.ticketId === n.ticketId);
    if (t) { setSelected(t); setTab("tickets"); }
  };

  const onTicketChanged = (t: SupportTicket) => {
    setSelected(t);
    setTickets((prev) => prev.map((x) => (x.ticketId === t.ticketId ? t : x)));
    refresh();
  };

  const onCreated = (t: SupportTicket) => {
    setSelected(t);
    setTab("tickets");
    refresh();
  };

  return (
    <div style={{ display: "flex", height: "100%", background: C.bg, fontFamily: C.fontSans }}>
      <AppNav activeRoute="help" />
      <main style={{ flex: 1, overflow: "auto", padding: "28px 36px" }}>
        <div style={{ maxWidth: 760 }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>✦ आपका कार्यक्षेत्र</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.fg, margin: "0 0 4px" }}>सहायता केंद्र</h1>
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 20px" }}>
            Smart Shikka चैट में पूछें या मानव सहायता के लिए टिकट बनाएँ।
          </p>
          {access.canUseInbox && (
            <div style={{ marginTop: -12, marginBottom: 16 }}>
              <button onClick={() => { window.location.hash = "#support"; }} style={btnStyle(C)}>
                सपोर्ट इनबॉक्स (स्टाफ़)
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
            {([["help", "सहायता"], ["tickets", `मेरे टिकट${tickets.length ? ` (${tickets.length})` : ""}`]] as const).map(([id, label]) => (
              <button key={id} onClick={() => { setTab(id); if (id === "tickets") setSelected(null); }} style={{
                padding: "8px 16px", border: "none", borderBottom: tab === id ? `2px solid ${C.accent}` : "2px solid transparent",
                background: "transparent", color: tab === id ? C.accentFg : C.muted, fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}>{label}</button>
            ))}
          </div>

          {unread > 0 && (
            <div style={{ marginBottom: 16, border: `1px solid ${C.border}`, borderRadius: 8, background: C.card, overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: C.accentFg, borderBottom: `1px solid ${C.border}` }}>
                {unread} नई सूचनाएँ
              </div>
              {notifs.filter((n) => n.readAt === undefined).slice(0, 5).map((n) => (
                <div key={n.notificationId} onClick={() => void openNotificationTicket(n)} style={{
                  padding: "8px 14px", fontSize: 11, color: C.fg, cursor: "pointer",
                  borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between",
                }}>
                  <span>{typeof n.payload?.subject === "string" ? n.payload.subject : n.eventType}</span>
                  <span style={{ color: C.muted }}>{fmtTs(n.createdAt)}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "help" && token && <CreateTicketForm token={token} onCreated={onCreated} />}

          {tab === "tickets" && token && (
            selected ? (
              <TicketDetail token={token} ticket={selected} onChanged={onTicketChanged} onBack={() => setSelected(null)} />
            ) : (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", background: C.card }}>
                {loading && <div style={{ padding: "24px 14px", textAlign: "center", fontSize: 12, color: C.muted }}>लोड हो रहे हैं...</div>}
                {!loading && tickets.length === 0 && (
                  <div style={{ padding: "32px 14px", textAlign: "center", fontSize: 12, color: C.muted }}>
                    अभी कोई टिकट नहीं। सहायता टैब से टिकट बनाएँ या चैट में पूछें।
                  </div>
                )}
                {tickets.map((t, i) => (
                  <div key={t.ticketId} onClick={() => setSelected(t)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer",
                    borderBottom: i < tickets.length - 1 ? `1px solid ${C.border}` : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{t.ticketNumber} · {fmtTs(t.updatedAt)}</div>
                    </div>
                    <StatusChip status={t.status} C={C} />
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </main>
    </div>
  );
}
